import React, { forwardRef, useCallback, useEffect, useState } from 'react'
import BaseModal from '../../../components/BaseModal';
import {Form, Button, Popover } from 'antd';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import Vocabulary from 'components/Vocabulary';
import ProductAPI from 'restAPI/ProductAPI';
import SubProductAPI from 'restAPI/SubProductAPI';
import SubProductEnvironmentTypeAPI from 'restAPI/SubProductEnvironmentTypeAPI';
import { MODAL_SIZE_MEDIUM } from 'constants/Layout';
import _ from "lodash";
import ModalUtil from 'utils/modalUtil';
import UserAPI from 'restAPI/UserAPI';
import AlertsAPI from 'restAPI/AlertsAPI';
import message from '../../../components/CustomMessage';
import CustomCollapse from "components/CustomCollapse";
import { STATUS_OPTIONS } from 'constants/NotificationConstants';
import { PermissionSetMap } from 'constants/RBACConstants';
import AuthorisedContainer from '../../../components/AuthorisedContainer';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import {toolNameForAPI} from 'views/Integrations/GenericPullAndPushToolView';
import { InfoIcon } from 'components/Icons';
import { hasPermission } from 'utils/authService';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';

export const ToolNotificationPopup = forwardRef(({name, notificationData, updateData, afterClose}, ref) => {
    const activeUser = useActiveUserContext();
    const [form] = Form.useForm();
    const [data, setData] = useState(getDefaultValues(notificationData, activeUser));
    const isEdit = !_.isUndefined(notificationData);
    const toolName = toolNameForAPI(name);
    const [environments, setEnvironments] = useState([]);
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    
    useEffect(() =>{
        if(!data.subProduct || data.subProduct === "All"){
            return;
        }
        const {promise, cancel} = SubProductEnvironmentTypeAPI.getAllQuick(data.subProduct);
        promise.then(setEnvironments);
        return cancel;
    }, [data.subProduct]);

    const createNotification = async () => {
        await form.validateFields();
        try{
            let data = notificationData?.id ? {...notificationData,...form.getFieldsValue()} : form.getFieldsValue()
            data.source = "INTEGRATION";
            data.toolName = toolName;
            data = _.pickBy(data, (value) => value !== "All");
            if(hasDisabledEnvironment){
                data.environment = environments.find(env => env.name === envValue)?.id;
            }
            await AlertsAPI[!isEdit ? "createAlertNotification" : "updateAlertNotification"](data, notificationData?.id).promise;
            message.success(`Notification ${isEdit ? "Edited": "Created"} Successfully`);
            updateData && updateData();
        } catch(err) {
            console.log(err);
            return Promise.reject("Error");
        }
    }
    
    const onChange = (values) => {
        setData({...data, ...values})
    };

    const notifyWhenTooltip =
        <ul>
            <li>Operation Status Down Notification: ArmorCode provides a default notification specifically for API-based configurations. It activates whenever ArmorCode encounters connection issues with a tool, whether due to authentication errors or timeouts.</li>
            <li>No New Reports Notification: ArmorCode provides a default notificationalert specifically for API-based configurations. It triggers when ArmorCode attempts to retrieve new reports from a tool but finds no updated reports available.</li>
            <li>Configuration Change Notification: ArmorCode provides a default notification for API and Push-based configurations. It activates when a user toggles a configuration option on the UI (located in the Configuration table under the "Config" column).</li>
            <li>Tool Configuration Disabled Notification: ArmorCode provides a default notification specifically for Push-based configurations. It triggers when a user attempts to execute a script, but the push-based configuration is disabled (Configuration table with the "Config" toggle).</li>
        </ul>;


    const mainFields = [
        {
            name: "sourceId",
            label: <>Notify When <Popover content={notifyWhenTooltip} overlayStyle={{ width: 600 }} placement='bottomLeft'><InfoIcon className='m-l-xs' /></Popover></>,
            placeholder: "Notify When",
            type: FieldTypes.SELECT,
            required: true,
            colSpan: 12,
            props: {
                api: AlertsAPI.getAlertNotificationSource,
                apiParams: toolName,
                parseSelectOptions: customParseSelectOptions,
                autoFocus: true
            },
            disabled: notificationData && notificationData.id,
        }, {
            name: "title",
            label: "Notification Title",
            rules: [
                {required: true, message: "Title is required"},
                {min: 5, message: "Title should be atleast 5 characters"},
                {max: 100,message: "Title should be maximum 100 characters"}],
            colSpan: 12
        }, {
            name: "severity",
            label: <Vocabulary name="severity" />,
            type: FieldTypes.SELECT,
            colSpan: 12,
            required: true,
            options: ["Critical", "High", "Medium", "Low", "Info"].map(s => ({label: s, value: s.toUpperCase()}))
        },{
            name: "description",
            label: "Description",
            required: true,
            colSpan: 24,
            type: FieldTypes.TEXT_AREA
        }
    ]

    const additionalFields = [{
            name: "product",
            label: <Vocabulary name="product" />,
            type: FieldTypes.SELECT,
            colSpan: 12,
            rules: [{required: true, message: <><Vocabulary name="product" /> is required</>}],
            props: {
                api: ProductAPI.getAllQuick,
                onChange: (e) => {setData({...data, product: e}); form.setFieldsValue({subProduct: null, environment: null})}
            },
            disabled: notificationData && notificationData.id
        }, {
            name: "subProduct",
            label: <Vocabulary name="subProduct" />,
            type: FieldTypes.SELECT,
            colSpan: 12,
            props: {
                allowClear: true,
                placeholder: "All",
                api: getSubproductList,
                apiParams: data.product,
                onChange: (e) => {
                    setData({...data, subProduct: e});
                    form.setFieldsValue({environment: null});
                    if (!e || e === "All") {
                        setEnvironments([]); // Clear environments when subProduct is cleared
                    }
                }
            },
            disabled: notificationData && notificationData.id
        }, ...(hasDisabledEnvironment ? [] : [{
            name: "environment",
            label: "Environment",
            type: FieldTypes.SELECT,
            colSpan: 12,
            options: environments,
            props: {
                allowClear: true,
                placeholder: "All",
                onChange: (e) => setData({...data, environment: e})
            },
            disabled: notificationData && notificationData.id
        }]), {
            name: "watchers",
            label: "CC Users",
            type: FieldTypes.MULTI_SELECT,
            colSpan: 12,
            required: true,
            help: "Only the selected users will receive an email for this notification.",
            props: {
                api: useCallback(() => UserAPI.getAll(data.product === 'All' ? undefined : data.product), [data.product]),
                parseSelectOptions: customUserOptions,
                optionFilterProp: "label"
            }
        }, 
        // {
        //     name: "maxSnooze",
        //     label: "Snooze",
        //     type: FieldTypes.SELECT,
        //     colSpan: 12,
        //     options: SNOOZE_OPTIONS,
        //     required: true,
        // }, 
        {
            name: "alertStatus",
            label: "Notification Status",
            type: FieldTypes.SELECT,
            options: STATUS_OPTIONS,
            colSpan: 12,
            required: true
        }
    ]

    const modalProps = {
        title: `${isEdit ? "Edit" : "Create"} Notification`, 
        width: MODAL_SIZE_MEDIUM,
        onOk: createNotification,
        okButtonProps: {
            disabled: !hasPermission(isEdit ? PermissionSetMap.UPDATE_SLA : PermissionSetMap.CREATE_SLA)
        }
    }

    return (
        <BaseModal
            ref={ref}
            modalProps={modalProps}
            afterClose={afterClose}
        >
            <Form 
                form={form}
                layout="vertical"
                onChange={onChange}
                initialValues={data}
            >
                {generateFormFields(mainFields)}
            </Form>
            <CustomCollapse 
                header="Additional Settings"
                Key="additionalSettings"   
                description={
                    <Form 
                    form={form}
                    layout="vertical"
                    onChange={onChange}
                    initialValues={data}
                    >
                    {generateFormFields(additionalFields)}
                    </Form>
                } 
                type="info"
                props={{destroyInactivePanel: true}}
            />
        </BaseModal>
    )
})

const ToolNotificationButton = ({toolName, ...props}) => {
    const onShow = () => {
        ModalUtil.show({
            content: <ToolNotificationPopup name={toolName} {...props}/>,
        })
    }
    
    return (<AuthorisedContainer permission={PermissionSetMap.CREATE_SLA}><Button onClick={onShow} size="small">Add Notification</Button></AuthorisedContainer>)
}
export default ToolNotificationButton;

function getSubproductList(productId){
    if(!productId || productId === "All"){
        return Promise.resolve([]);
    } else {
        return SubProductAPI.getAllQuick(productId);
    }
}

function customParseSelectOptions(options) {
    return (Object.entries(options) || []).map((item) => ({label: item[1], value: +item[0]}))
}

function customUserOptions(options) {
    return (options || []).map(option => ({ label: option.displayName, value: option.id }))
}

function getDefaultValues(data = {}, activeUser) {
    if(activeUser.accessOnAllProduct){
        data.product = data.product || "All";
        data.subProduct = data.subProduct || "All";
        data.environment = data.environment || "All";
    }
    return _.pickBy(data, _.identity);
}